import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import { track } from '../utils/track';

const CartContext = createContext(null);

// Guest cart: visitors can add courses or a package before creating an account. The cart
// lives in localStorage as course ids (plus the bundle state); details are looked up from
// the public catalog. The moment they sign up or log in, it's moved into their server cart
// and the guest copy is cleared. Account creation now happens at checkout, not at "Add".
const GUEST_KEY = 'se_guest_cart';
const readGuest = () => {
  try {
    const g = JSON.parse(localStorage.getItem(GUEST_KEY));
    return g && Array.isArray(g.courseIds) ? g : { courseIds: [], bundleState: null };
  } catch { return { courseIds: [], bundleState: null }; }
};
const writeGuest = (g) => {
  try {
    if (!g.courseIds.length) localStorage.removeItem(GUEST_KEY);
    else localStorage.setItem(GUEST_KEY, JSON.stringify(g));
  } catch { /* storage blocked — cart just won't persist */ }
};

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBundle, setIsBundle] = useState(() => {
    try { return localStorage.getItem('se_cart_bundle') === 'true'; } catch { return false; }
  });
  const catalogRef = useRef(null);

  const setBundleFlag = (on) => {
    setIsBundle(on);
    try { on ? localStorage.setItem('se_cart_bundle', 'true') : localStorage.removeItem('se_cart_bundle'); } catch { /* ignore */ }
  };

  // Public catalog (published courses), cached for building guest cart items.
  const getCatalog = useCallback(async () => {
    if (catalogRef.current) return catalogRef.current;
    const res = await api.get('/courses');
    catalogRef.current = res.data.courses || [];
    return catalogRef.current;
  }, []);

  const loadGuestItems = useCallback(async () => {
    const g = readGuest();
    if (!g.courseIds.length) { setItems([]); return; }
    const catalog = await getCatalog();
    const byId = Object.fromEntries(catalog.map(c => [c.id, c]));
    setItems(g.courseIds.filter(id => byId[id]).map(id => {
      const c = byId[id];
      return {
        id: `guest-${id}`, course_id: id, title: c.title, price: c.price, credit_hours: c.credit_hours,
        credit_type: c.credit_type, thumbnail_url: c.thumbnail_url, short_description: c.short_description, state: c.state,
      };
    }));
  }, [getCatalog]);

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      if (!user) { await loadGuestItems(); return; }
      const res = await api.get('/cart');
      setItems(res.data.items);
      if (res.data.items.length === 0) setBundleFlag(false);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [user, loadGuestItems]);

  // On sign-in/sign-up: move any guest cart into the account first, then load it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (user) {
        const g = readGuest();
        if (g.courseIds.length) {
          setLoading(true); // hold the cart page until the move finishes
          try {
            if (g.bundleState) {
              await api.post('/bundles/add-to-cart', { bundleId: 'complete-ce-package', state: g.bundleState });
              setBundleFlag(true);
            } else {
              for (const courseId of g.courseIds) {
                // 409 = already owns it; anything else, keep going with the rest
                await api.post('/cart/add', { courseId }).catch(() => {});
              }
            }
          } catch { /* fall through — they can re-add */ }
          writeGuest({ courseIds: [], bundleState: null });
        }
      }
      if (!cancelled) await fetchCart();
    })();
    return () => { cancelled = true; };
  }, [user, fetchCart]);

  const addToCart = async (courseId) => {
    track('add_to_cart', courseId);
    if (!user) {
      const g = readGuest();
      // Adding an individual course leaves package mode, same as for signed-in users.
      const ids = g.bundleState ? [] : g.courseIds;
      if (!ids.includes(courseId)) ids.push(courseId);
      writeGuest({ courseIds: ids, bundleState: null });
      setBundleFlag(false);
      await loadGuestItems();
      return;
    }
    await api.post('/cart/add', { courseId });
    setBundleFlag(false);
    await fetchCart();
  };

  // A bundle belongs to one state — pass the 2-letter code so the server adds that
  // state's courses and prices them from that state's package.
  const addBundle = async (state = 'AZ') => {
    track('add_package', state);
    if (!user) {
      const res = await api.get(`/bundles?state=${encodeURIComponent(state)}`);
      const bundle = (res.data.bundles || [])[0];
      if (!bundle) throw Object.assign(new Error('No package available'), { response: { data: { error: 'This package isn’t available right now.' } } });
      writeGuest({ courseIds: bundle.courses.map(c => c.id), bundleState: state });
      setBundleFlag(true);
      await loadGuestItems();
      return;
    }
    await api.post('/bundles/add-to-cart', { bundleId: 'complete-ce-package', state });
    setBundleFlag(true);
    await fetchCart();
  };

  const removeFromCart = async (courseId) => {
    if (!user) {
      const g = readGuest();
      writeGuest({ courseIds: g.courseIds.filter(id => id !== courseId), bundleState: null });
      setBundleFlag(false);
      setItems(prev => prev.filter(i => i.course_id !== courseId));
      return;
    }
    await api.delete(`/cart/${courseId}`);
    setItems(prev => prev.filter(i => i.course_id !== courseId));
  };

  const clearCart = async () => {
    if (user) await api.delete('/cart');
    else writeGuest({ courseIds: [], bundleState: null });
    setItems([]);
    setBundleFlag(false);
  };

  const clearBundle = () => setBundleFlag(false);

  const isInCart = (courseId) => items.some(i => i.course_id === courseId);

  const total = items.reduce((sum, i) => sum + parseFloat(i.price), 0);

  return (
    <CartContext.Provider value={{ items, loading, isBundle, isGuest: !user, addToCart, addBundle, removeFromCart, clearCart, clearBundle, isInCart, total, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
